import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, ArrowUpRight, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STATUS_STYLE = {
  submitted:  "bg-amber-50 text-amber-800 border-amber-200",
  approved:   "bg-blue-50 text-blue-700 border-blue-200",
  rejected:   "bg-red-50 text-red-700 border-red-200",
  reimbursed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}
function fmtEuro(n) {
  const s = (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${s} €`;
}


export default function ExpenseReportsList() {
  const { t } = useTranslation();
  const { hasPermission, session } = useAuth();
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const canValidate = hasPermission("expenses.validate");
  const canWrite = hasPermission("expenses.write");

  useEffect(() => {
    (async () => {
      try {
        const [m, a] = await Promise.all([
          api.get("/expense-reports?mine=true"),
          canValidate ? api.get("/expense-reports") : Promise.resolve({ data: { reports: [] } }),
        ]);
        setMine(m.data.reports || []);
        setAll(a.data.reports || []);
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.tenant?.id]);

  const toValidate = all.filter((r) => r.status === "submitted");

  return (
    <div className="space-y-6" data-testid="expense-reports-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
            {t("expenseReports.title")}
          </div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("expenseReports.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t("expenseReports.subtitle")}</p>
        </div>
        {canWrite && (
          <Button asChild data-testid="new-expense-report-btn">
            <Link to="/app/expenses/new">
              <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} />
              {t("expenseReports.newReport")}
            </Link>
          </Button>
        )}
      </div>

      <Tabs defaultValue={canValidate && toValidate.length > 0 ? "toValidate" : "mine"} className="w-full">
        <TabsList data-testid="expense-reports-tabs">
          <TabsTrigger value="mine" data-testid="tab-mine">
            {t("expenseReports.tabs.mine")}
            {mine.length > 0 && (
              <span className="ml-1.5 text-[10px] text-slate-500">({mine.length})</span>
            )}
          </TabsTrigger>
          {canValidate && (
            <TabsTrigger value="toValidate" data-testid="tab-to-validate">
              {t("expenseReports.tabs.toValidate")}
              {toValidate.length > 0 && (
                <span className="ml-1.5 inline-flex items-center rounded-full bg-amber-500 text-white px-1.5 text-[10px] font-medium">
                  {toValidate.length}
                </span>
              )}
            </TabsTrigger>
          )}
          {canValidate && (
            <TabsTrigger value="all" data-testid="tab-all">
              {t("expenseReports.tabs.all")}
              {all.length > 0 && <span className="ml-1.5 text-[10px] text-slate-500">({all.length})</span>}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="mine" className="mt-4">
          <ReportsTable rows={mine} emptyKey="mine" showAuthor={false} loading={loading} t={t} />
        </TabsContent>
        {canValidate && (
          <TabsContent value="toValidate" className="mt-4">
            <ReportsTable rows={toValidate} emptyKey="toValidate" showAuthor loading={loading} t={t} />
          </TabsContent>
        )}
        {canValidate && (
          <TabsContent value="all" className="mt-4">
            <ReportsTable rows={all} emptyKey="all" showAuthor loading={loading} t={t} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}


function ReportsTable({ rows, emptyKey, showAuthor, loading, t }) {
  if (loading) return <Card className="p-8 text-sm text-slate-500">Chargement…</Card>;
  if (rows.length === 0) {
    return (
      <Card className="p-8 text-center border-dashed border-slate-300 bg-white"
            data-testid={`expense-reports-empty-${emptyKey}`}>
        <ClipboardList className="h-8 w-8 mx-auto text-slate-300" strokeWidth={1.5} />
        <div className="mt-3 text-sm text-slate-600">{t(`expenseReports.empty.${emptyKey}`)}</div>
      </Card>
    );
  }
  return (
    <Card className="border-slate-200 rounded-xl bg-white overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("expenseReports.cols.period")}</TableHead>
            {showAuthor && <TableHead>{t("expenseReports.cols.author")}</TableHead>}
            <TableHead>{t("expenseReports.cols.items")}</TableHead>
            <TableHead className="text-right">{t("expenseReports.cols.total")}</TableHead>
            <TableHead>{t("expenseReports.cols.status")}</TableHead>
            <TableHead>{t("expenseReports.cols.submittedAt")}</TableHead>
            <TableHead className="text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`row-report-${r.id}`}>
              <TableCell className="font-medium text-slate-900">{r.period_label}</TableCell>
              {showAuthor && (
                <TableCell className="text-slate-600 text-sm">{r.created_by_email || "—"}</TableCell>
              )}
              <TableCell className="text-slate-600 text-sm">{r.items?.length || 0}</TableCell>
              <TableCell className="text-right text-sm">{fmtEuro(r.total_amount)}</TableCell>
              <TableCell>
                <Badge variant="outline"
                       className={cn("text-[10px] font-medium", STATUS_STYLE[r.status])}>
                  {t(`expenseReports.status.${r.status}`)}
                </Badge>
              </TableCell>
              <TableCell className="text-slate-500 text-xs">{fmtDate(r.created_at)}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/app/expenses/${r.id}`} data-testid={`open-report-${r.id}`}>
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
